import { useMemo } from 'react';
import { getSession, useGetAllProfileQuery, useGetFeedSpaces } from '@onesource/services';
import { isFeatureFlagEnabled } from '@onesource/components';
import { EFeatureFlagEnum } from 'src/hooks/use-feature-flag-utils';
import { topSectionItems, getBottomSectionItems, socialLinks } from './sidebar-data';
import type { SidebarSection, SocialLink, SidebarItem, SidebarSubItem } from './types';
import type { TFeedSpaceItem, TSpaceSubscriptionItem, TUserAccessMap } from '@onesource/schemas';
import { ALL_APPS } from 'src/config';
import { hasAppAccess } from 'src/utils/app-access';
import { CommunityOutlined, Exercise } from '@onesource/icons';
import { useClassicAreaLink } from './use-classic-area-link';

interface UseSidebarDataReturn {
  sidebarData: SidebarSection[];
  socialLinks: SocialLink[];
  isLoading: boolean;
}

/**
 * Build sidebar items from spaces data
 */
const buildSpaceItems = (spaces: TSpaceSubscriptionItem[] | undefined, showLocked: boolean): SidebarSubItem[] => {
  if (!Array.isArray(spaces) || spaces.length === 0) {
    return [];
  }

  const filtered = showLocked ? spaces : spaces.filter(space => space.hasAccess);

  return filtered.map(space => ({
    id: space.id,
    label: space.name,
    // Single-lesson spaces have a dedicated leaf route; containers land on the space home.
    path:
      space.spaceType === 'single_lesson'
        ? `/community-hub/spaces/${space.id}/lesson`
        : `/community-hub/spaces/${space.id}`,
    isLocked: !space.hasAccess,
  }));
};

/**
 * Build Community menu items from FEED sub-spaces.
 * hasAccess is returned by the backend (getFeedSpaces endpoint) using user_access.
 */
const buildCommunityItems = (feedSpaces: TFeedSpaceItem[] | undefined, showLocked: boolean): SidebarSubItem[] => {
  if (!Array.isArray(feedSpaces) || feedSpaces.length === 0) {
    return [];
  }

  const filtered = showLocked ? feedSpaces : feedSpaces.filter(s => s.hasAccess);

  return filtered.map(space => ({
    id: `community-${space.id}`,
    label: space.name,
    path: `/community-hub/spaces/${space.id}/community`,
    isLocked: !space.hasAccess,
  }));
};

/**
 * Build app items from access (FIT, RYC, etc.)
 * Only shows apps that have a URL and the user has permissions for that app.
 */
const buildAppItems = (access: TUserAccessMap | undefined): SidebarSubItem[] => {
  if (!access) {
    return [];
  }

  return ALL_APPS.filter(app => {
    if (!app.url) {
      return false;
    }
    return hasAppAccess(access, app.id);
  }).map(app => ({
    id: app.id,
    label: app.name.subscribed,
    externalApp: {
      appId: app.id,
      url: `${app.url}`,
    },
  }));
};

export const useSidebarData = (): UseSidebarDataReturn => {
  const token = getSession();
  const { data: profileData, isLoading } = useGetAllProfileQuery({ token });
  const { data: feedSpaces } = useGetFeedSpaces();
  const { handleClassicPanelClick } = useClassicAreaLink();

  const sidebarData = useMemo<SidebarSection[]>(() => {
    // Get spaces from profile subscription
    const spacesSubscription = profileData?.subscription?.SPACES;
    const spaces = Array.isArray(spacesSubscription?.spaces) ? spacesSubscription.spaces : [];

    const showLockedSpaces = isFeatureFlagEnabled(EFeatureFlagEnum.SHOW_LOCKED_SPACES);

    // Build app items from access (FIT, RYC, etc.)
    const appItems = buildAppItems(profileData?.access);

    // Build space items from profile data
    const spaceItems = buildSpaceItems(spaces, showLockedSpaces);

    const allChildren = [...appItems, ...spaceItems];

    const spacesItem: SidebarItem = {
      id: 'programs',
      label: 'Programs',
      icon: Exercise,
      path: '/apps', // Clicking on "Programs" parent navigates to apps page
      children: allChildren,
    };

    // Main section items
    const mainItems: SidebarItem[] = [];

    mainItems.push(spacesItem);

    // Community menu uses feed spaces — FEED sub-spaces within container spaces.
    // hasAccess is computed by the backend using user_access.
    const communityChildren = buildCommunityItems(feedSpaces, showLockedSpaces);
    if (communityChildren.length > 0) {
      const communityItem: SidebarItem = {
        id: 'community',
        label: 'Community',
        icon: CommunityOutlined,
        path: communityChildren[0]?.path,
        children: communityChildren,
      };
      mainItems.push(communityItem);
    }

    return [
      {
        id: 'top',
        items: topSectionItems,
        showDivider: true,
      },
      {
        id: 'main',
        items: mainItems,
        showDivider: true,
      },
      {
        id: 'bottom',
        items: getBottomSectionItems(() => handleClassicPanelClick()),
      },
    ];
  }, [profileData, feedSpaces, handleClassicPanelClick]);

  return {
    sidebarData,
    socialLinks,
    isLoading,
  };
};
